import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import type { LearningSession } from '../types/database'
import { Sidebar } from './Sidebar'
import { useAuth } from '../contexts/AuthContext'

export function LearningSessionList() {
    const { roles } = useAuth()
    const navigate = useNavigate()
    const [sessions, setSessions] = useState<LearningSession[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    const fetchSessions = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('learning_sessions')
                .select('*')
                .order('created_at', { ascending: false })

            if (error) throw error
            setSessions(data || [])
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch sessions')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchSessions()
    }, [])

    const handleDelete = async (id: number) => {
        if (!confirm('정말 삭제하시겠습니까?')) return

        try {
            const { error } = await supabase
                .from('learning_sessions')
                .delete()
                .eq('id', id)

            if (error) throw error
            await fetchSessions()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete session')
        }
    }

    if (loading) {
        return (
            <div className="relative flex min-h-screen w-full bg-background-light font-display">
                <Sidebar />
                <main className="ml-64 flex flex-1 items-center justify-center">
                    <div className="flex items-center gap-2 text-gray-600">
                        <span className="material-symbols-outlined animate-spin">progress_activity</span>
                        <span className="text-lg">로딩 중...</span>
                    </div>
                </main>
            </div>
        )
    }

    return (
        <div className="relative flex min-h-screen w-full bg-background-light font-display">
            <Sidebar />
            <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
                <div className="w-full max-w-7xl mx-auto">
                    <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
                        <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">학습 세션 관리</h1>
                        {roles.includes('admin') && (
                            <button
                                onClick={() => navigate('/learning-sessions/new')}
                                className="flex min-w-[84px] items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold leading-normal tracking-wide hover:bg-primary/90 transition-colors"
                            >
                                <span className="material-symbols-outlined">add</span>
                                <span className="truncate">새 세션 만들기</span>
                            </button>
                        )}
                    </header>

                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                            <span className="material-symbols-outlined text-base">error</span>
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                        {sessions.length === 0 ? (
                            <div className="text-center py-12">
                                <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">school</span>
                                <p className="text-gray-500">등록된 학습 세션이 없습니다.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                세션 이름
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                AI 사용
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                상태
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                등록일
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                작업
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {sessions.map((session) => (
                                            <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="h-[72px] px-4 py-2 text-sm font-medium text-gray-800">
                                                    {session.name}
                                                </td>
                                                <td className="h-[72px] px-4 py-2 text-sm text-gray-600">
                                                    {session.ai_enabled ? (
                                                        <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-1 rounded-full text-xs font-medium">
                                                            <span className="material-symbols-outlined text-sm">check_circle</span>
                                                            Enabled
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-gray-600 bg-gray-100 px-2 py-1 rounded-full text-xs font-medium">
                                                            <span className="material-symbols-outlined text-sm">cancel</span>
                                                            Disabled
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="h-[72px] px-4 py-2 text-sm text-gray-600">
                                                    {session.status > 0 ? 'Published' : 'Draft'}
                                                </td>
                                                <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                                                    {new Date(session.created_at).toLocaleDateString('ko-KR', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                    })}
                                                </td>
                                                <td className="h-[72px] px-4 py-2">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => navigate(`/solutions?session=${session.id}`)}
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                                                        >
                                                            <span className="material-symbols-outlined text-sm">draw</span>
                                                            <span>드로잉</span>
                                                        </button>
                                                        {roles.includes('admin') && (
                                                            <>
                                                                <button
                                                                    onClick={() => navigate(`/learning-sessions/${session.id}`)}
                                                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                                                >
                                                                    <span className="material-symbols-outlined text-sm">edit</span>
                                                                    <span>수정</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(session.id)}
                                                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                                                >
                                                                    <span className="material-symbols-outlined text-sm">delete</span>
                                                                    <span>삭제</span>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    )
}
